import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { ClockIcon, AlertTriangleIcon, CheckCircleIcon, UserIcon, TicketIcon, TrendingUpIcon, FilterIcon, SettingsIcon, PlusIcon, TrashIcon, MessageSquareIcon, TimerIcon, TableIcon, LayoutGridIcon, EyeIcon, EyeOffIcon, ArrowUpDownIcon, CalendarIcon, BarChartIcon, LogOutIcon } from "lucide-react";

// Auth imports
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return children;
};

// Priority badge styles
const getPriorityBadge = (priority) => {
  const styles = {
    Critical: "bg-red-100 text-red-800 border-red-200",
    High: "bg-orange-100 text-orange-800 border-orange-200", 
    Medium: "bg-blue-100 text-blue-800 border-blue-200",
    Low: "bg-gray-100 text-gray-800 border-gray-200"
  };
  return styles[priority] || styles.Medium;
};

// Status badge styles
const getStatusBadge = (status) => {
  const styles = {
    Open: "bg-green-100 text-green-800 border-green-200",
    "In Progress": "bg-yellow-100 text-yellow-800 border-yellow-200",
    Resolved: "bg-blue-100 text-blue-800 border-blue-200",
    Closed: "bg-gray-100 text-gray-800 border-gray-200"
  };
  return styles[status] || styles.Open;
};

// Customer Ticket Submission Form
const CustomerForm = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    customer_name: "",
    customer_email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/tickets`, formData);
      setSubmitted(true);
      setFormData({ title: "", description: "", customer_name: "", customer_email: "" });
    } catch (error) {
      console.error("Error submitting ticket:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-green-800">Ticket Submitted Successfully!</CardTitle>
            <CardDescription>
              Your support request has been received and is being processed by our intelligent triage system.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-3">
            <Button onClick={() => setSubmitted(false)} className="w-full">
              Submit Another Ticket
            </Button>
            <Link to="/dashboard" className="block">
              <Button variant="outline" className="w-full">
                View Ticket Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="relative">
          <div className="flex flex-col items-center text-center">
            <CardTitle className="text-2xl font-bold text-slate-800">Submit Support Ticket</CardTitle>
            <CardDescription className="mt-2 max-w-md">
              Our intelligent system will automatically prioritize and route your request to the right team.
            </CardDescription>
          </div>
          <Link to="/dashboard" className="absolute top-0 right-0" style={{ marginRight: '8px', marginTop: '10px' }}>
            <Button variant="outline" size="sm">
              Ticket Dashboard
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">Your Name</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="transition-all duration-200 focus:scale-[1.02]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email Address</Label>
                <Input
                  id="customer_email"
                  name="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                  className="transition-all duration-200 focus:scale-[1.02]"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="title">Issue Title</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief description of your issue"
                required
                className="transition-all duration-200 focus:scale-[1.02]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Detailed Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Please provide as much detail as possible about your issue. Include any error messages, steps to reproduce, or relevant information."
                rows={6}
                required
                className="transition-all duration-200 focus:scale-[1.02] resize-none"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full h-12 text-lg font-medium transition-all duration-200 hover:scale-[1.02] disabled:scale-100"
            >
              {isSubmitting ? "Submitting..." : "Submit Ticket"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Ticket Dashboard (formerly Agent Dashboard)
const TicketDashboard = () => {
  const { user, logout, isManager, isAgent } = useAuth();
  
  const [allTickets, setAllTickets] = useState([]);
  const [smartQueue, setSmartQueue] = useState([]);
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all"); // Agent filter state
  const [teamFilter, setTeamFilter] = useState("all"); // New team filter state
  const [customerFilter, setCustomerFilter] = useState("all"); // New customer filter state
  const [deliveryPredictions, setDeliveryPredictions] = useState({}); // Delivery predictions by ticket ID
  
  // View toggle state
  const [viewMode, setViewMode] = useState("card"); // "card", "table", or "gantt"
  
  // Table view states
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [visibleColumns, setVisibleColumns] = useState([
    "title", "customer_name", "status", "priority", 
    "assigned_agent_name", "assigned_team", "created_at", "delivery_prediction"
  ]);
  const [columnOrder, setColumnOrder] = useState([
    "title", "customer_name", "status", "priority", 
    "assigned_agent_name", "assigned_team", "created_at", "delivery_prediction"
  ]);
  
  // Modal states
  const [cannedResponseModal, setCannedResponseModal] = useState({ isOpen: false, ticketId: null });
  const [effortTrackingModal, setEffortTrackingModal] = useState({ isOpen: false, ticketId: null });
  const [ticketEditModal, setTicketEditModal] = useState({ isOpen: false, ticket: null });

  // Gantt Chart State
  const [ganttData, setGanttData] = useState(null);
  const [ganttLoading, setGanttLoading] = useState(false);
  const [selectedTicketForReassign, setSelectedTicketForReassign] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, smartQueueRes, statsRes, agentsRes, teamsRes, predictionsRes] = await Promise.all([
        axios.get(`${API}/tickets`), // Get all tickets for filtering
        axios.get(`${API}/tickets/smart-queue`), // Get smart queue for default ordering
        axios.get(`${API}/tickets/stats/overview`),
        axios.get(`${API}/agents`), // Get available agents
        axios.get(`${API}/teams`), // Get available teams
        axios.get(`${API}/delivery-predictions`) // Get delivery predictions
      ]);
      
      setAllTickets(ticketsRes.data);
      setSmartQueue(smartQueueRes.data);
      setStats(statsRes.data);
      setAgents(agentsRes.data);
      setTeams(teamsRes.data);
      
      // Convert predictions array to object keyed by ticket_id for easy lookup
      const predictionsMap = {};
      predictionsRes.data.predictions.forEach(prediction => {
        predictionsMap[prediction.ticket_id] = prediction;
      });
      setDeliveryPredictions(predictionsMap);
      
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchGanttData = async () => {
    setGanttLoading(true);
    try {
      const response = await axios.get(`${API}/gantt-data`);
      setGanttData(response.data);
    } catch (error) {
      console.error("Error fetching Gantt data:", error);
    } finally {
      setGanttLoading(false);
    }
  };

  const reassignTicketFromGantt = async (ticketId, newAgentId) => {
    try {
      await axios.post(`${API}/gantt-reassign`, null, {
        params: {
          ticket_id: ticketId,
          new_agent_id: newAgentId
        }
      });
      // Refresh Gantt data to reflect changes
      await fetchGanttData();
      // Also refresh main ticket data
      await fetchData();
    } catch (error) {
      console.error("Error reassigning ticket:", error);
      alert("Failed to reassign ticket. Please try again.");
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      await axios.patch(`${API}/tickets/${ticketId}`, { status: newStatus });
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating ticket:", error);
    }
  };

  const assignTicket = async (ticketId, assignedTo, assignedTeam) => {
    try {
      const updateData = {};
      
      // Handle agent assignment
      if (assignedTo !== undefined) {
        updateData.assigned_to = assignedTo;
        
        // Auto-assign team based on agent's team (unless explicitly overridden)
        if (assignedTo && assignedTeam === undefined) {
          const selectedAgent = agents.find(agent => agent.id === assignedTo);
          if (selectedAgent && selectedAgent.team) {
            updateData.assigned_team = selectedAgent.team;
          }
        }
        
        // Clear team if agent is unassigned (unless team is explicitly provided)
        if (!assignedTo && assignedTeam === undefined) {
          updateData.assigned_team = "";
        }
      }
      
      // Handle explicit team assignment
      if (assignedTeam !== undefined) {
        updateData.assigned_team = assignedTeam;
      }
      
      await axios.patch(`${API}/tickets/${ticketId}`, updateData);
      fetchData(); // Refresh data including delivery predictions
    } catch (error) {
      console.error("Error assigning ticket:", error);
    }
  };

  // Helper function to format delivery date
  const formatDeliveryDate = (prediction) => {
    if (!prediction) return "Calculating...";
    
    if (prediction.status === "unassigned") {
      return "Not assigned yet";
    }
    
    if (prediction.status === "completed") {
      return "Completed";
    }
    
    if (!prediction.predicted_delivery_date) {
      return "Calculating...";
    }
    
    const deliveryDate = new Date(prediction.predicted_delivery_date);
    const now = new Date();
    const diffTime = deliveryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const dateStr = deliveryDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    if (diffDays < 0) {
      return `${dateStr} (Overdue)`;
    } else if (diffDays === 0) {
      return `${dateStr} (Today)`;
    } else if (diffDays === 1) {
      return `${dateStr} (Tomorrow)`;
    } else if (diffDays <= 7) {
      return `${dateStr} (${diffDays} days)`;
    } else {
      return dateStr;
    }
  };

  // Helper function to get delivery status color
  const getDeliveryStatusColor = (prediction) => {
    if (!prediction || prediction.status === "unassigned") {
      return "text-gray-500";
    }
    
    if (prediction.status === "completed") {
      return "text-green-600";
    }
    
    if (!prediction.predicted_delivery_date) {
      return "text-gray-500";
    }
    
    const deliveryDate = new Date(prediction.predicted_delivery_date);
    const now = new Date();
    const diffTime = deliveryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "text-red-600"; // Overdue
    if (diffDays <= 1) return "text-orange-600"; // Due today/tomorrow
    if (diffDays <= 3) return "text-yellow-600"; // Due soon
    return "text-green-600"; // On track
  };

  // Get unique customers from all tickets
  const getUniqueCustomers = () => {
    const customers = new Set();
    allTickets.forEach(ticket => {
      if (ticket.customer_name && ticket.customer_email) {
        customers.add(`${ticket.customer_name} (${ticket.customer_email})`);
      }
    });
    return Array.from(customers).sort();
  };

  // Filter Gantt data based on selected filters
  const getFilteredGanttData = () => {
    if (!ganttData || !ganttData.agents) return ganttData;

    // Agent filtering
    let filteredAgents = ganttData.agents;
    
    if (agentFilter !== "all") {
      filteredAgents = ganttData.agents.filter(agent => agent.agent_id === agentFilter);
    }

    // Team filtering
    if (teamFilter !== "all") {
      filteredAgents = filteredAgents.filter(agent => {
        const agentDetails = agents.find(a => a.id === agent.agent_id);
        const agentTeam = agentDetails?.team;
        
        if (teamFilter === "unassigned") {
          return !agentTeam;
        } else {
          return agentTeam === teamFilter;
        }
      });
    }

    // Customer filtering (filter tickets within each agent)
    if (customerFilter !== "all") {
      filteredAgents = filteredAgents.map(agent => {
        const filteredTickets = agent.tickets.filter(ganttTicket => {
          const ticketDetails = tickets.find(t => t.id === ganttTicket.ticket_id);
          return ticketDetails && ticketDetails.customer_name === customerFilter;
        });
        
        return {
          ...agent,
          tickets: filteredTickets
        };
      }).filter(agent => agent.tickets.length > 0); // Remove agents with no tickets after customer filtering
    }

    return {
      ...ganttData,
      agents: filteredAgents
    };
  };

  // Available table columns configuration
  const availableColumns = {
    title: { label: "Title", sortable: true },
    customer_name: { label: "Customer", sortable: true },
    customer_email: { label: "Email", sortable: true },
    status: { label: "Status", sortable: true },
    priority: { label: "Priority", sortable: true },
    assigned_agent_name: { label: "Agent", sortable: true },
    assigned_team: { label: "Team", sortable: true },
    created_at: { label: "Created", sortable: true },
    updated_at: { label: "Updated", sortable: true },
    delivery_prediction: { label: "Expected Delivery", sortable: false }
  };

  // Sort tickets for table view
  const getSortedTickets = (tickets) => {
    return [...tickets].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle special cases
      if (sortField === "delivery_prediction") {
        const aPred = deliveryPredictions[a.id];
        const bPred = deliveryPredictions[b.id];
        aValue = aPred?.predicted_delivery_date || "9999-12-31";
        bValue = bPred?.predicted_delivery_date || "9999-12-31";
      }
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      
      // Convert to strings for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(col => col !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Render table cell content
  const renderCellContent = (ticket, columnKey, onTitleClick) => {
    switch (columnKey) {
      case "title":
        return (
          <div 
            className="font-medium text-gray-900 max-w-xs truncate cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => onTitleClick(ticket)}
          >
            {ticket.title}
          </div>
        );
      
      case "customer_name":
        return (
          <div>
            <div className="font-medium text-gray-900">{ticket.customer_name}</div>
            <div className="text-sm text-gray-500">{ticket.customer_email}</div>
          </div>
        );
      
      case "customer_email":
        return (
          <div className="text-sm text-gray-600">{ticket.customer_email}</div>
        );
      
      case "status":
        return (
          <Badge className={getStatusBadge(ticket.status)}>
            {ticket.status}
          </Badge>
        );
      
      case "priority":
        return (
          <Badge className={getPriorityBadge(ticket.priority)}>
            {ticket.priority}
          </Badge>
        );
      
      case "assigned_agent_name":
        return ticket.assigned_agent_name ? (
          <div className="flex items-center space-x-2">
            <UserIcon className="w-4 h-4 text-blue-600" />
            <span className="text-blue-600 font-medium">{ticket.assigned_agent_name}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">Unassigned</span>
        );
      
      case "assigned_team":
        return ticket.assigned_team ? (
          <Badge variant="outline">{ticket.assigned_team}</Badge>
        ) : (
          <span className="text-gray-400 italic">No team</span>
        );
      
      case "created_at":
        return (
          <div className="text-sm">
            {new Date(ticket.created_at).toLocaleDateString()}
            <div className="text-xs text-gray-500">
              {new Date(ticket.created_at).toLocaleTimeString()}
            </div>
          </div>
        );
      
      case "updated_at":
        return (
          <div className="text-sm">
            {new Date(ticket.updated_at).toLocaleDateString()}
            <div className="text-xs text-gray-500">
              {new Date(ticket.updated_at).toLocaleTimeString()}
            </div>
          </div>
        );
      
      case "delivery_prediction":
        const prediction = deliveryPredictions[ticket.id];
        return (
          <div className="text-sm">
            <div className={`font-medium ${getDeliveryStatusColor(prediction)}`}>
              {formatDeliveryDate(prediction)}
            </div>
            {prediction && prediction.status === "assigned" && (
              <div className="text-xs text-gray-500">
                {prediction.remaining_effort_hours.toFixed(1)}h • #{prediction.queue_position}
              </div>
            )}
            {prediction && prediction.status === "unassigned" && (
              <div className="text-xs text-gray-500">
                ~{prediction.remaining_effort_hours.toFixed(1)}h est.
              </div>
            )}
          </div>
        );
      
      default:
        return ticket[columnKey] || "";
    }
  };

  const getFilteredTickets = () => {
    const sourceTickets = filter === "all" ? smartQueue : allTickets;
    
    let filteredTickets = [];
    
    if (filter === "all") {
      filteredTickets = smartQueue;
    } else if (filter === "high-priority") {
      filteredTickets = allTickets.filter(ticket => ["High", "Critical"].includes(ticket.priority));
    } else {
      // For status filters, match exactly
      filteredTickets = allTickets.filter(ticket => {
        if (filter === "open") return ticket.status === "Open";
        if (filter === "in_progress") return ticket.status === "In Progress";
        if (filter === "resolved") return ticket.status === "Resolved";
        if (filter === "closed") return ticket.status === "Closed";
        return true;
      });
    }
    
    // Apply agent filter
    if (agentFilter !== "all") {
      if (agentFilter === "unassigned") {
        filteredTickets = filteredTickets.filter(ticket => !ticket.assigned_to);
      } else {
        filteredTickets = filteredTickets.filter(ticket => ticket.assigned_to === agentFilter);
      }
    }
    
    // Apply team filter
    if (teamFilter !== "all") {
      if (teamFilter === "unassigned") {
        filteredTickets = filteredTickets.filter(ticket => !ticket.assigned_team);
      } else {
        filteredTickets = filteredTickets.filter(ticket => ticket.assigned_team === teamFilter);
      }
    }
    
    // Apply customer filter
    if (customerFilter !== "all") {
      filteredTickets = filteredTickets.filter(ticket => 
        ticket.customer_name === customerFilter || ticket.customer_email === customerFilter
      );
    }
    
    return filteredTickets;
  };

  const filteredTickets = getFilteredTickets();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Ticket Dashboard</h1>
            <p className="text-slate-600">
              Intelligent ticket management system
              {isAgent && " - Agent View"}
              {isManager && " - Manager View"}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-800">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role} • {user?.email}</p>
            </div>
            <div className="flex space-x-2">
              {isManager && (
                <>
                  <Link to="/reports">
                    <Button variant="outline" className="flex items-center space-x-2">
                      <BarChartIcon className="w-4 h-4" />
                      <span>Reports</span>
                    </Button>
                  </Link>
                  <Link to="/customization">
                    <Button variant="outline" className="flex items-center space-x-2">
                      <SettingsIcon className="w-4 h-4" />
                      <span>Settings</span>
                    </Button>
                  </Link>
                </>
              )}
              <Link to="/">
                <Button variant="outline">Customer Portal</Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={logout}
                className="flex items-center space-x-2 text-red-600 hover:text-red-700"
              >
                <LogOutIcon className="w-4 h-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* View Toggle and Column Settings */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            {/* View Toggle */}
            <div className="flex items-center space-x-2 bg-white p-1 rounded-lg border border-gray-200">
              <Button
                variant={viewMode === "card" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("card")}
                className="flex items-center space-x-2"
              >
                <LayoutGridIcon className="w-4 h-4" />
                <span>Card View</span>
              </Button>
              <Button
                variant={viewMode === "table" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("table")}
                className="flex items-center space-x-2"
              >
                <TableIcon className="w-4 h-4" />
                <span>Table View</span>
              </Button>
              <Button
                variant={viewMode === "gantt" ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setViewMode("gantt");
                  if (viewMode !== "gantt") {
                    fetchGanttData();
                  }
                }}
                className="flex items-center space-x-2"
              >
                <CalendarIcon className="w-4 h-4" />
                <span>Gantt View</span>
              </Button>
            </div>
            
            <div className="text-sm text-gray-600">
              {viewMode === "card" ? "📊 Card Layout" : viewMode === "table" ? "📋 Tabular Layout" : "📅 Timeline View"}
            </div>
          </div>
          
          {/* Column Settings (only show in table view) */}
          {viewMode === "table" && (
            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-gray-700">Columns:</Label>
              <div className="flex items-center space-x-1 bg-white p-2 rounded-lg border border-gray-200">
                {Object.entries(availableColumns).map(([key, config]) => (
                  <Button
                    key={key}
                    variant={visibleColumns.includes(key) ? "default" : "ghost"}
                    size="sm"
                    onClick={() => toggleColumnVisibility(key)}
                    className="text-xs h-8"
                  >
                    {visibleColumns.includes(key) ? (
                      <EyeIcon className="w-3 h-3 mr-1" />
                    ) : (
                      <EyeOffIcon className="w-3 h-3 mr-1" />
                    )}
                    {config.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <TicketIcon className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
                    <p className="text-xs text-blue-600">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <ClockIcon className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-green-800">{stats.open}</p>
                    <p className="text-xs text-green-600">Open</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <TrendingUpIcon className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-800">{stats.in_progress}</p>
                    <p className="text-xs text-yellow-600">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold text-purple-800">{stats.resolved}</p>
                    <p className="text-xs text-purple-600">Resolved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{stats.closed}</p>
                    <p className="text-xs text-gray-600">Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangleIcon className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-2xl font-bold text-orange-800">{stats.high_priority}</p>
                    <p className="text-xs text-orange-600">High Priority</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangleIcon className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold text-red-800">{stats.critical}</p>
                    <p className="text-xs text-red-600">Critical</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Controls */}
        <div className="mb-6 space-y-4">
          {/* Status Filter Tabs */}
          <div className="flex space-x-2 bg-white p-1 rounded-lg border">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "all" 
                  ? "bg-blue-600 text-white" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              All Tickets
            </button>
            <button
              onClick={() => setFilter("open")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "open" 
                  ? "bg-blue-600 text-white" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              Open
            </button>
            <button
              onClick={() => setFilter("in_progress")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "in_progress" 
                  ? "bg-blue-600 text-white" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setFilter("high-priority")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "high-priority" 
                  ? "bg-blue-600 text-white" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              High Priority
            </button>
            <button
              onClick={() => setFilter("resolved")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "resolved" 
                  ? "bg-blue-600 text-white" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              Resolved
            </button>
          </div>
          
          {/* Filter Dropdowns - Show for all views */}
          <div className="flex items-center space-x-6">
            {/* Agent Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-slate-700">Agent:</Label>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="All agents..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                      <span>All Agents</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="unassigned">
                    <div className="flex items-center space-x-2">
                      <AlertTriangleIcon className="w-4 h-4 text-orange-500" />
                      <span>Unassigned</span>
                    </div>
                  </SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-3 h-3 text-blue-600" />
                        </div>
                        <div>
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-xs text-gray-500 ml-2">({agent.team || 'No team'})</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Team Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-slate-700">Team:</Label>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All teams..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center space-x-2">
                      <TicketIcon className="w-4 h-4 text-gray-500" />
                      <span>All Teams</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="unassigned">
                    <div className="flex items-center space-x-2">
                      <AlertTriangleIcon className="w-4 h-4 text-orange-500" />
                      <span>No Team</span>
                    </div>
                  </SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.name}>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: team.color }}
                        ></div>
                        <span className="font-medium">{team.name}</span>
                        <span className="text-xs text-gray-500">({team.description?.substring(0, 20)}...)</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-slate-700">Customer:</Label>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-60">
                  <SelectValue placeholder="All customers..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                      <span>All Customers</span>
                    </div>
                  </SelectItem>
                  {getUniqueCustomers().map(customer => {
                    const [name, email] = customer.split(' (');
                    const cleanEmail = email?.replace(')', '');
                    return (
                      <SelectItem key={customer} value={name}>
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-3 h-3 text-green-600" />
                          </div>
                          <div>
                            <span className="font-medium">{name}</span>
                            <span className="text-xs text-gray-500 ml-2 block">{cleanEmail}</span>
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Filter Results Info */}
            <div className="text-sm text-slate-600 bg-slate-100 px-3 py-2 rounded-lg">
              <strong>{filteredTickets.length}</strong> tickets
              {agentFilter !== "all" && (
                <span className="text-blue-600">
                  {agentFilter === "unassigned" 
                    ? " • Unassigned" 
                    : ` • ${agents.find(a => a.id === agentFilter)?.name || 'Unknown Agent'}`
                  }
                </span>
              )}
              {teamFilter !== "all" && (
                <span className="text-green-600">
                  {teamFilter === "unassigned" 
                    ? " • No Team" 
                    : ` • ${teamFilter} Team`
                  }
                </span>
              )}
              {customerFilter !== "all" && (
                <span className="text-purple-600">
                  {` • ${customerFilter}`}
                </span>
              )}
            </div>
            
            {/* Clear Filters Button */}
            {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all") && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setAgentFilter("all");
                  setTeamFilter("all");
                  setCustomerFilter("all");
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Smart Queue Alert */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertTriangleIcon className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Smart Queue:</strong> Tickets are automatically prioritized by urgency, priority level, and age. 
            Focus on the top tickets first for optimal workflow.
          </AlertDescription>
        </Alert>

        {/* Tickets Display */}
        {viewMode === "card" ? (
          /* Card View */
          <div className="space-y-4">
            {filteredTickets.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <TicketIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No tickets found matching your filter.</p>
                </CardContent>
              </Card>
            ) : (
              filteredTickets.map((ticket) => (
                <Card key={ticket.id} className="hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle 
                          className="text-lg mb-2 cursor-pointer hover:text-blue-600 transition-colors"
                          onClick={() => setTicketEditModal({ isOpen: true, ticket })}
                        >
                          {ticket.title}
                        </CardTitle>
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className={getPriorityBadge(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          <Badge className={getStatusBadge(ticket.status)}>
                            {ticket.status}
                          </Badge>
                          {ticket.assigned_team && (
                            <Badge variant="outline">{ticket.assigned_team}</Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <span>{ticket.customer_name}</span>
                          <span>{ticket.customer_email}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                        
                        {/* Assignment Display */}
                        <div className="flex items-center justify-between text-sm mb-2">
                          <div className="flex items-center space-x-2">
                            {ticket.assigned_agent_name && (
                              <div className="flex items-center space-x-1">
                                <UserIcon className="w-4 h-4 text-blue-600" />
                                <span className="text-blue-600 font-medium">{ticket.assigned_agent_name}</span>
                              </div>
                            )}
                            {ticket.assigned_team && (
                              <div className="flex items-center space-x-1">
                                <span className="text-gray-500">Team:</span>
                                <span className="text-gray-700 font-medium">{ticket.assigned_team}</span>
                              </div>
                            )}
                            {!ticket.assigned_agent_name && !ticket.assigned_team && (
                              <span className="text-gray-400 italic">Unassigned</span>
                            )}
                          </div>
                        </div>

                        {/* Delivery Prediction Display */}
                        <div className="bg-gray-50 p-3 rounded-lg mb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700">Expected Delivery:</span>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm font-medium ${getDeliveryStatusColor(deliveryPredictions[ticket.id])}`}>
                                {formatDeliveryDate(deliveryPredictions[ticket.id])}
                              </div>
                              {deliveryPredictions[ticket.id] && deliveryPredictions[ticket.id].status === "assigned" && (
                                <div className="text-xs text-gray-500">
                                  {deliveryPredictions[ticket.id].remaining_effort_hours.toFixed(1)}h remaining
                                  {deliveryPredictions[ticket.id].queue_position > 1 && (
                                    <span> • #{deliveryPredictions[ticket.id].queue_position} in queue</span>
                                  )}
                                </div>
                              )}
                              {deliveryPredictions[ticket.id] && deliveryPredictions[ticket.id].status === "unassigned" && (
                                <div className="text-xs text-gray-500">
                                  ~{deliveryPredictions[ticket.id].remaining_effort_hours.toFixed(1)}h estimated
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4 line-clamp-2">{ticket.description}</p>
                    
                    {ticket.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {ticket.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <Separator className="my-4" />
                    
                    {/* Assignment Controls */}
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <Label className="text-xs text-gray-600 mb-1 block">Assign to Agent</Label>
                          <Select 
                            value={ticket.assigned_to || ""} 
                            onValueChange={(value) => assignTicket(ticket.id, value === "unassign" ? "" : value, undefined)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select agent..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassign">
                                <span className="text-gray-500">Unassigned</span>
                              </SelectItem>
                              {agents.map(agent => (
                                <SelectItem key={agent.id} value={agent.id}>
                                  <div className="flex items-center space-x-2">
                                    <span>{agent.name}</span>
                                    <span className="text-xs text-blue-600">
                                      → {agent.team || 'No team'}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex-1">
                          <Label className="text-xs text-gray-600 mb-1 block">Team</Label>
                          <Select 
                            value={ticket.assigned_team || ""} 
                            onValueChange={(value) => assignTicket(ticket.id, undefined, value === "unassign" ? "" : value)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select team..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassign">
                                <span className="text-gray-500">No Team</span>
                              </SelectItem>
                              {teams.map(team => (
                                <SelectItem key={team.id} value={team.name}>
                                  <div className="flex items-center space-x-2">
                                    <div 
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: team.color }}
                                    ></div>
                                    <span>{team.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        {ticket.status === "Open" && (
                          <Button 
                            size="sm" 
                            onClick={() => updateTicketStatus(ticket.id, "In Progress")}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Start Working
                          </Button>
                        )}
                        {ticket.status === "In Progress" && (
                          <Button 
                            size="sm" 
                            onClick={() => updateTicketStatus(ticket.id, "Resolved")}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Mark Resolved
                          </Button>
                        )}
                        {ticket.status === "Resolved" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateTicketStatus(ticket.id, "Closed")}
                          >
                            Close Ticket
                          </Button>
                        )}
                        
                        {/* New Feature Buttons */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setCannedResponseModal({ isOpen: true, ticketId: ticket.id })}
                          className="flex items-center space-x-1"
                        >
                          <MessageSquareIcon className="w-3 h-3" />
                          <span>Responses</span>
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setEffortTrackingModal({ isOpen: true, ticketId: ticket.id })}
                          className="flex items-center space-x-1"
                        >
                          <TimerIcon className="w-3 h-3" />
                          <span>Effort</span>
                        </Button>
                      </div>
                      
                      {/* Quick Assignment Buttons */}
                      <div className="flex space-x-1">
                        {!ticket.assigned_to && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => {
                              // Assign to first available agent in same team or any agent
                              const availableAgent = agents.find(agent => 
                                agent.team === ticket.assigned_team || !ticket.assigned_team
                              ) || agents[0];
                              if (availableAgent) {
                                assignTicket(ticket.id, availableAgent.id, undefined);
                              }
                            }}
                            className="text-xs"
                          >
                            Auto-assign
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Ticket Response Section */}
                    <TicketResponseSection ticketId={ticket.id} />
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : viewMode === "table" ? (
          /* Table View */
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <TicketIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No tickets found matching your filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {columnOrder.filter(col => visibleColumns.includes(col)).map((columnKey) => (
                        <th
                          key={columnKey}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => availableColumns[columnKey].sortable && handleSort(columnKey)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{availableColumns[columnKey].label}</span>
                            {availableColumns[columnKey].sortable && (
                              <ArrowUpDownIcon className="w-3 h-3" />
                            )}
                            {sortField === columnKey && (
                              <span className={`text-blue-600 ${sortDirection === 'asc' ? '↑' : '↓'}`}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedTickets(filteredTickets).map((ticket) => (
                      <tr key={ticket.id} className="hover:bg-gray-50">
                        {columnOrder.filter(col => visibleColumns.includes(col)).map((columnKey) => (
                          <td key={columnKey} className="px-6 py-4 whitespace-nowrap">
                            {renderCellContent(ticket, columnKey, (clickedTicket) => setTicketEditModal({ isOpen: true, ticket: clickedTicket }))}
                          </td>
                        ))}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setCannedResponseModal({ isOpen: true, ticketId: ticket.id })}
                              className="flex items-center space-x-1"
                            >
                              <MessageSquareIcon className="w-3 h-3" />
                            </Button>
                            
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEffortTrackingModal({ isOpen: true, ticketId: ticket.id })}
                              className="flex items-center space-x-1"
                            >
                              <TimerIcon className="w-3 h-3" />
                            </Button>
                            
                            <Select value={ticket.assigned_to || "unassigned"} onValueChange={(value) => assignTicket(ticket.id, value === "unassigned" ? null : value, undefined)}>
                              <SelectTrigger className="w-32">
                                <SelectValue placeholder="Assign..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">
                                  <span className="text-gray-500">Unassign</span>
                                </SelectItem>
                                {agents.map(agent => (
                                  <SelectItem key={agent.id} value={agent.id}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{agent.name}</span>
                                      <span className="text-xs text-blue-600">→ {agent.team || 'No team'}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Gantt View */
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {ganttLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading Gantt chart...</p>
              </div>
            ) : ganttData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Project Timeline</h3>
                  
                  {/* Active Filters Indicator */}
                  {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all") && (
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-gray-600">Active filters:</span>
                      {agentFilter !== "all" && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          Agent: {agents.find(a => a.id === agentFilter)?.name || agentFilter}
                        </Badge>
                      )}
                      {teamFilter !== "all" && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          Team: {teamFilter === "unassigned" ? "No Team" : teamFilter}
                        </Badge>
                      )}
                      {customerFilter !== "all" && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          Customer: {customerFilter}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Summary Table */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                    <BarChartIcon className="w-5 h-5 text-blue-600 mr-2" />
                    Workload Summary
                    {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all") && (
                      <Badge variant="outline" className="ml-2 text-xs bg-blue-100 text-blue-700">
                        Filtered
                      </Badge>
                    )}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">Agent</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">Tickets</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Total Hours</th>
                          <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Avg Hours/Ticket</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredGanttData().agents.map((agent, index) => {
                          const totalHours = agent.tickets.reduce((sum, ticket) => sum + ticket.remaining_effort_hours, 0);
                          const avgHours = agent.tickets.length > 0 ? totalHours / agent.tickets.length : 0;
                          
                          return (
                            <tr key={agent.agent_id} className={index % 2 === 0 ? "bg-white" : "bg-blue-25"}>
                              <td className="py-2 px-3">
                                <div className="flex items-center space-x-2">
                                  <UserIcon className="w-4 h-4 text-blue-600" />
                                  <span className="font-medium text-gray-800">{agent.agent_name}</span>
                                </div>
                              </td>
                              <td className="text-center py-2 px-3">
                                <Badge variant="outline" className="text-xs">
                                  {agent.tickets.length}
                                </Badge>
                              </td>
                              <td className="text-right py-2 px-3">
                                <span className="font-semibold text-gray-900">
                                  {totalHours.toFixed(1)}h
                                </span>
                              </td>
                              <td className="text-right py-2 px-3">
                                <span className="text-gray-600">
                                  {avgHours.toFixed(1)}h
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Total Row */}
                        <tr className="border-t border-blue-300 bg-blue-100 font-semibold">
                          <td className="py-3 px-3">
                            <div className="flex items-center space-x-2">
                              <BarChartIcon className="w-4 h-4 text-blue-700" />
                              <span className="font-bold text-gray-900">TOTAL</span>
                            </div>
                          </td>
                          <td className="text-center py-3 px-3">
                            <Badge className="bg-blue-600 text-white text-xs">
                              {getFilteredGanttData().agents.reduce((sum, agent) => sum + agent.tickets.length, 0)}
                            </Badge>
                          </td>
                          <td className="text-right py-3 px-3">
                            <span className="font-bold text-blue-900 text-lg">
                              {getFilteredGanttData().agents.reduce((sum, agent) => 
                                sum + agent.tickets.reduce((ticketSum, ticket) => ticketSum + ticket.remaining_effort_hours, 0), 0
                              ).toFixed(1)}h
                            </span>
                          </td>
                          <td className="text-right py-3 px-3">
                            <span className="text-blue-700 font-medium">
                              {(() => {
                                const filteredData = getFilteredGanttData();
                                const totalTickets = filteredData.agents.reduce((sum, agent) => sum + agent.tickets.length, 0);
                                const totalHours = filteredData.agents.reduce((sum, agent) => 
                                  sum + agent.tickets.reduce((ticketSum, ticket) => ticketSum + ticket.remaining_effort_hours, 0), 0
                                );
                                return totalTickets > 0 ? (totalHours / totalTickets).toFixed(1) : '0.0';
                              })()}h
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-blue-200">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>
                        <strong>{getFilteredGanttData().agents.length}</strong> active agents
                        {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all") && (
                          <span className="text-blue-600"> (filtered)</span>
                        )}
                      </span>
                      <span>•</span>
                      <span>
                        <strong>{getFilteredGanttData().agents.reduce((sum, agent) => sum + agent.tickets.length, 0)}</strong> assigned tickets
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Timeline: <strong>{new Date(ganttData.timeline_start).toLocaleDateString()}</strong> → <strong>{new Date(ganttData.timeline_end).toLocaleDateString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    {getFilteredGanttData().agents && getFilteredGanttData().agents.map((agent) => (
                      <div key={agent.agent_id} className="mb-6 border-b border-gray-200 pb-4">
                        <div className="flex items-center mb-3">
                          <UserIcon className="w-5 h-5 text-blue-600 mr-2" />
                          <h4 className="font-medium text-gray-800">{agent.agent_name}</h4>
                          <Badge variant="outline" className="ml-2">{agent.tickets.length} tickets</Badge>
                        </div>
                        <div className="space-y-2">
                          {agent.tickets && agent.tickets.length > 0 ? (
                            agent.tickets.map((ticket) => (
                              <div 
                                key={ticket.ticket_id} 
                                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                                onClick={() => setSelectedTicketForReassign({
                                  ticketId: ticket.ticket_id,
                                  ticketTitle: ticket.title,
                                  currentAgentId: ticket.assigned_agent_id,
                                  currentAgentName: ticket.assigned_agent_name
                                })}
                              >
                                <div className="flex-1">
                                  <div className="font-medium text-sm text-gray-800">{ticket.title}</div>
                                  <div className="text-xs text-gray-500">
                                    Priority: {ticket.priority} • Status: {ticket.status} • Queue: #{ticket.queue_position}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-gray-700">
                                    {ticket.remaining_effort_hours.toFixed(1)}h remaining
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Start: {new Date(ticket.start_date).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Due: {new Date(ticket.end_date).toLocaleDateString()}
                                  </div>
                                </div>
                                <Badge className={getPriorityBadge(ticket.priority)}>
                                  {ticket.priority}
                                </Badge>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-gray-500 italic p-3">No tickets assigned</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all")
                    ? "No tickets match the selected filters. Try adjusting your filter criteria."
                    : "No Gantt data available. Please ensure tickets are assigned to agents."
                  }
                </p>
                <div className="flex space-x-2 justify-center mt-4">
                  <Button 
                    onClick={fetchGanttData} 
                    variant="outline"
                  >
                    Refresh Gantt Data
                  </Button>
                  {(agentFilter !== "all" || teamFilter !== "all" || customerFilter !== "all") && (
                    <Button 
                      onClick={() => {
                        setAgentFilter("all");
                        setTeamFilter("all");
                        setCustomerFilter("all");
                      }}
                      variant="outline"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Modals */}
        <CannedResponseModal 
          isOpen={cannedResponseModal.isOpen}
          onClose={() => setCannedResponseModal({ isOpen: false, ticketId: null })}
          onSelectResponse={(content) => {
            // Instead of alert, we could integrate with the response form
            // For now, show the content and suggest copying it
            const message = `Selected canned response:\n\n"${content}"\n\nYou can now copy this text and paste it into the response field for the ticket.`;
            alert(message);
          }}
        />
        
        <EffortTrackingModal 
          isOpen={effortTrackingModal.isOpen}
          onClose={() => setEffortTrackingModal({ isOpen: false, ticketId: null })}
          ticketId={effortTrackingModal.ticketId}
        />
        
        <TicketEditModal 
          isOpen={ticketEditModal.isOpen}
          onClose={() => setTicketEditModal({ isOpen: false, ticket: null })}
          ticket={ticketEditModal.ticket}
          agents={agents}
          teams={teams}
          onUpdate={fetchData}
        />

        {/* Gantt Reassignment Modal */}
        {selectedTicketForReassign && (
          <Dialog open={!!selectedTicketForReassign} onOpenChange={() => setSelectedTicketForReassign(null)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Reassign Ticket</DialogTitle>
                <DialogDescription>
                  Reassign "{selectedTicketForReassign.ticketTitle}" to a different agent
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Current Agent</Label>
                  <div className="p-2 bg-gray-100 rounded text-sm">
                    {selectedTicketForReassign.currentAgentName}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">New Agent</Label>
                  <Select onValueChange={(agentId) => {
                    reassignTicketFromGantt(selectedTicketForReassign.ticketId, agentId);
                    setSelectedTicketForReassign(null);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select new agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.filter(agent => agent.id !== selectedTicketForReassign.currentAgentId).map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          <div className="flex items-center space-x-2">
                            <UserIcon className="w-4 h-4" />
                            <span>{agent.name}</span>
                            <span className="text-xs text-blue-600">({agent.team || 'No team'})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

// Customization/Settings Page
// Email-to-Ticket Management Component
const EmailToTicketManagement = () => {
  const [emailToTicketStatus, setEmailToTicketStatus] = useState(null);
  const [emailMessages, setEmailMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [setupInstructions, setSetupInstructions] = useState(null);

  useEffect(() => {
    fetchEmailToTicketStatus();
    fetchEmailMessages();
  }, []);

  const fetchEmailToTicketStatus = async () => {
    try {
      const response = await axios.get(`${API}/email-to-ticket/status`);
      setEmailToTicketStatus(response.data);
    } catch (error) {
      console.error("Error fetching email-to-ticket status:", error);
      setEmailToTicketStatus({ monitoring_active: false, error: error.message });
    }
  };

  const fetchEmailMessages = async () => {
    try {
      const response = await axios.get(`${API}/email-messages`);
      setEmailMessages(response.data);
    } catch (error) {
      console.error("Error fetching email messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const processTestEmail = async () => {
    try {
      const response = await axios.post(`${API}/email-to-ticket/process-test-email`);
      alert(`✅ Test email processed successfully!\nTicket ID: ${response.data.ticket_id}\nEmail Message ID: ${response.data.email_message_id}`);
      // Refresh data
      fetchEmailToTicketStatus();
      fetchEmailMessages();
    } catch (error) {
      alert(`❌ Error processing test email: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getGmailSetupInstructions = async () => {
    try {
      const response = await axios.post(`${API}/email-to-ticket/setup-gmail`);
      setSetupInstructions(response.data);
      setShowSetupInstructions(true);
    } catch (error) {
      console.error("Error getting setup instructions:", error);
      alert(`❌ Error getting setup instructions: ${error.response?.data?.detail || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading email management data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquareIcon className="w-5 h-5" />
            <span>Email-to-Ticket Status</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {emailToTicketStatus ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Gmail Credentials</p>
                  <p className={`font-medium ${emailToTicketStatus.gmail_credentials_configured ? 'text-green-600' : 'text-red-600'}`}>
                    {emailToTicketStatus.gmail_credentials_configured ? '✅ Configured' : '❌ Not Configured'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Gmail Token</p>
                  <p className={`font-medium ${emailToTicketStatus.gmail_token_exists ? 'text-green-600' : 'text-red-600'}`}>
                    {emailToTicketStatus.gmail_token_exists ? '✅ Available' : '❌ Missing'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Monitoring Status</p>
                  <p className={`font-medium ${emailToTicketStatus.monitoring_active ? 'text-green-600' : 'text-yellow-600'}`}>
                    {emailToTicketStatus.monitoring_active ? '🟢 Active' : '🟡 Ready (Needs Setup)'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Monitor Email</p>
                  <p className="font-medium text-slate-800">
                    {emailToTicketStatus.configuration?.monitor_email || 'Not configured'}
                  </p>
                </div>
              </div>

              {emailToTicketStatus.statistics && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-2">Statistics</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Total Emails:</span>
                      <span className="ml-2 font-medium">{emailToTicketStatus.statistics.total_email_messages}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Tickets from Email:</span>
                      <span className="ml-2 font-medium">{emailToTicketStatus.statistics.total_tickets_from_email}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Recent (24h):</span>
                      <span className="ml-2 font-medium">{emailToTicketStatus.statistics.recent_emails_24h}</span>
                    </div>
                  </div>
                </div>
              )}

              {emailToTicketStatus.error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangleIcon className="h-4 w-4" />
                  <AlertDescription className="text-red-700">
                    Error: {emailToTicketStatus.error}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-slate-600">Unable to load status</p>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button 
          onClick={processTestEmail}
          className="flex items-center space-x-2"
        >
          <MessageSquareIcon className="w-4 h-4" />
          <span>Process Test Email</span>
        </Button>
        
        <Button 
          onClick={getGmailSetupInstructions}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <SettingsIcon className="w-4 h-4" />
          <span>Gmail Setup Instructions</span>
        </Button>
        
        <Button 
          onClick={() => { fetchEmailToTicketStatus(); fetchEmailMessages(); }}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <span>🔄 Refresh Data</span>
        </Button>
      </div>

      {/* Setup Instructions Modal */}
      {showSetupInstructions && setupInstructions && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center justify-between">
              Gmail OAuth 2.0 Setup Instructions
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSetupInstructions(false)}
              >
                ✕
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-blue-800 mb-2">Setup Steps:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
                  {setupInstructions.instructions?.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ol>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-slate-800 mb-2">Required OAuth Scopes:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {setupInstructions.required_scopes?.map((scope, index) => (
                    <li key={index} className="font-mono bg-slate-100 px-2 py-1 rounded">
                      {scope}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-slate-800 mb-2">Configuration Files:</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Credentials File:</strong> <code className="bg-slate-100 px-1 rounded">{setupInstructions.configuration?.credentials_file_path}</code></p>
                  <p><strong>Token File:</strong> <code className="bg-slate-100 px-1 rounded">{setupInstructions.configuration?.token_file_path}</code></p>
                  <p><strong>Monitor Email:</strong> <code className="bg-slate-100 px-1 rounded">{setupInstructions.configuration?.monitor_email}</code></p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquareIcon className="w-5 h-5" />
            <span>Email Messages ({emailMessages.length})</span>
          </CardTitle>
          <CardDescription>
            Recent email messages processed by the email-to-ticket system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailMessages.length > 0 ? (
            <div className="space-y-4">
              {emailMessages.slice(0, 10).map((message) => (
                <div key={message.id} className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h4 className="font-medium text-slate-800">{message.subject}</h4>
                        <Badge variant="outline" className="text-xs">
                          Ticket: {message.ticket_id}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-3">
                        <div>
                          <span className="font-medium">From:</span> {message.sender_name ? `${message.sender_name} <${message.sender_email}>` : message.sender_email}
                        </div>
                        <div>
                          <span className="font-medium">To:</span> {message.recipient_email}
                        </div>
                        <div>
                          <span className="font-medium">Received:</span> {formatDate(message.received_at)}
                        </div>
                        <div>
                          <span className="font-medium">Processed:</span> {formatDate(message.processed_at)}
                        </div>
                      </div>

                      {message.body_text && (
                        <div className="bg-white p-3 rounded border max-h-32 overflow-y-auto">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {message.body_text.length > 200 
                              ? `${message.body_text.substring(0, 200)}...` 
                              : message.body_text
                            }
                          </p>
                        </div>
                      )}

                      {(message.in_reply_to || message.references) && (
                        <div className="mt-2 text-xs text-slate-500">
                          <span className="font-medium">Threading:</span>
                          {message.in_reply_to && <span className="ml-2">Reply to: {message.in_reply_to}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {emailMessages.length > 10 && (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-600">
                    Showing 10 of {emailMessages.length} messages. 
                    <Button variant="link" className="px-1">View All Messages</Button>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquareIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No email messages found</p>
              <p className="text-sm text-slate-500 mt-2">
                Process a test email to see how the email-to-ticket system works
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CustomizationPage = () => {
  const { user, isManager } = useAuth();
  
  // Only managers can access this page
  if (!isManager) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only managers can access the customization settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/dashboard">
              <Button className="w-full">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showEmailConfigForm, setShowEmailConfigForm] = useState(false);
  
  // Form states
  const [newAgent, setNewAgent] = useState({
    name: "",
    email: "",
    role: "Agent",
    team: ""
  });
  
  const [newTeam, setNewTeam] = useState({
    name: "",
    description: "",
    color: "#3B82F6"
  });

  const [newCustomer, setNewCustomer] = useState({
    company_name: "",
    email_domains: "",
    po_number: "",
    valid_until: "",
    total_manhours: "",
    consumed_manhours: ""
  });

  const [emailConfigData, setEmailConfigData] = useState({
    helpdesk_email: "",
    smtp_server: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: ""
  });

  // User Management States
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(null);

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    confirm_password: "",
    role: "agent",
    agent_id: ""
  });

  const [passwordResetData, setPasswordResetData] = useState({
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, agentsRes, customersRes, usersRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/agents`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/users`)
      ]);
      
      setTeams(teamsRes.data);
      setAgents(agentsRes.data);
      setCustomers(customersRes.data);
      setUsers(usersRes.data);

      // Try to fetch email config (may not exist)
      try {
        const emailConfigRes = await axios.get(`${API}/email-config`);
        setEmailConfig(emailConfigRes.data);
        setEmailConfigData({
          helpdesk_email: emailConfigRes.data.helpdesk_email || "",
          smtp_server: emailConfigRes.data.smtp_server || "",
          smtp_port: emailConfigRes.data.smtp_port || 587,
          smtp_username: emailConfigRes.data.smtp_username || "",
          smtp_password: emailConfigRes.data.smtp_password || ""
        });
      } catch (error) {
        console.log("No email config found");
        setEmailConfig(null);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agents`, newAgent);
      setNewAgent({ name: "", email: "", role: "Agent", team: "" });
      setShowAgentForm(false);
      fetchData();
    } catch (error) {
      console.error("Error creating agent:", error);
      alert("Error creating agent. Please check if the team exists.");
    }
  };

  const createTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, newTeam);
      setNewTeam({ name: "", description: "", color: "#3B82F6" });
      setShowTeamForm(false);
      fetchData();
    } catch (error) {
      console.error("Error creating team:", error);
      alert("Error creating team.");
    }
  };

  const createCustomer = async (e) => {
    e.preventDefault();
    try {
      const customerData = {
        ...newCustomer,
        valid_until: new Date(newCustomer.valid_until).toISOString(),
        total_manhours: parseFloat(newCustomer.total_manhours),
        consumed_manhours: parseFloat(newCustomer.consumed_manhours || 0)
      };
      
      await axios.post(`${API}/customers`, customerData);
      setNewCustomer({
        company_name: "",
        email_domains: "",
        po_number: "",
        valid_until: "",
        total_manhours: "",
        consumed_manhours: ""
      });
      setShowCustomerForm(false);
      fetchData();
    } catch (error) {
      console.error("Error creating customer:", error);
      alert("Error creating customer. Please check all fields.");
    }
  };

  const deleteCustomer = async (customerId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      try {
        await axios.delete(`${API}/customers/${customerId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting customer:", error);
        alert("Error deleting customer.");
      }
    }
  };

  // User Management Functions
  const createUser = async (e) => {
    e.preventDefault();
    if (newUser.password !== newUser.confirm_password) {
      alert("Passwords do not match");
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, {
        name: newUser.name,
        email: newUser.email,
        username: newUser.username,
        password: newUser.password,
        role: newUser.role,
        agent_id: (newUser.agent_id && newUser.agent_id !== "none") ? newUser.agent_id : null
      });
      
      setShowUserForm(false);
      setNewUser({
        name: "",
        email: "",
        username: "",
        password: "",
        confirm_password: "",
        role: "agent",
        agent_id: ""
      });
      fetchData();
      alert("User created successfully!");
    } catch (error) {
      console.error("Error creating user:", error);
      alert(error.response?.data?.detail || "Error creating user.");
    }
  };

  const updateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        username: editingUser.username,
        role: editingUser.role,
        agent_id: editingUser.agent_id || null,
        is_active: editingUser.is_active
      });
      
      setEditingUser(null);
      fetchData();
      alert("User updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.response?.data?.detail || "Error updating user.");
    }
  };

  const resetUserPassword = async (userId) => {
    if (passwordResetData.new_password !== passwordResetData.confirm_password) {
      alert("Passwords do not match");
      return;
    }

    if (passwordResetData.new_password.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    try {
      await axios.post(`${API}/users/${userId}/reset-password`, {
        new_password: passwordResetData.new_password
      });
      
      setShowPasswordReset(null);
      setPasswordResetData({
        new_password: "",
        confirm_password: ""
      });
      alert("Password reset successfully!");
    } catch (error) {
      console.error("Error resetting password:", error);
      alert(error.response?.data?.detail || "Error resetting password.");
    }
  };

  const deleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to deactivate user ${userName}?`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        fetchData();
        alert("User deactivated successfully!");
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(error.response?.data?.detail || "Error deleting user.");
      }
    }
  };

  const saveEmailConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/email-config`, emailConfigData);
      setShowEmailConfigForm(false);
      fetchData();
      alert("Email configuration saved successfully!");
    } catch (error) {
      console.error("Error saving email config:", error);
      alert("Error saving email configuration.");
    }
  };

  const deleteAgent = async (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to deactivate ${agentName}? They will no longer appear in assignment dropdowns.`)) {
      try {
        await axios.delete(`${API}/agents/${agentId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting agent:", error);
        alert("Error deactivating agent.");
      }
    }
  };

  const deleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete the ${teamName} team? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/teams/${teamId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting team:", error);
        if (error.response?.data?.detail) {
          alert(error.response.data.detail);
        } else {
          alert("Error deleting team.");
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading customization settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Customization Settings</h1>
            <p className="text-slate-600">Manage your agents and teams</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline">Back to Ticket Dashboard</Button>
          </Link>
        </div>
      </div>

      <div className="p-6 max-w-6xl mx-auto">
        <Tabs defaultValue="teams" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="teams">Teams</TabsTrigger>
            <TabsTrigger value="agents">Agents</TabsTrigger>
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="email">Email Config</TabsTrigger>
            <TabsTrigger value="email-tickets">Email Management</TabsTrigger>
          </TabsList>

          {/* Teams Tab */}
          <TabsContent value="teams" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">Team Management</h2>
              <Button 
                onClick={() => setShowTeamForm(!showTeamForm)}
                className="flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Team</span>
              </Button>
            </div>

            {/* Team Creation Form */}
            {showTeamForm && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Create New Team</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createTeam} className="space-y-4">
                    <div>
                      <Label htmlFor="team-name">Team Name</Label>
                      <Input
                        id="team-name"
                        value={newTeam.name}
                        onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
                        placeholder="Enter team name"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="team-description">Description</Label>
                      <Textarea
                        id="team-description"
                        value={newTeam.description}
                        onChange={(e) => setNewTeam({...newTeam, description: e.target.value})}
                        placeholder="Team description"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="team-color">Team Color</Label>
                      <Input
                        id="team-color"
                        type="color"
                        value={newTeam.color}
                        onChange={(e) => setNewTeam({...newTeam, color: e.target.value})}
                        className="w-20 h-10"
                      />
                    </div>

                    <div className="flex space-x-2">
                      <Button type="submit">Create Team</Button>
                      <Button type="button" variant="outline" onClick={() => setShowTeamForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Teams List */}
            <div className="space-y-3">
              {teams.map((team) => (
                <Card key={team.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: team.color }}
                        ></div>
                        <div>
                          <h3 className="font-medium text-slate-800">{team.name}</h3>
                          <p className="text-sm text-slate-600">{team.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTeam(team.id, team.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">Agent Management</h2>
              <Button 
                onClick={() => setShowAgentForm(!showAgentForm)}
                className="flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Agent</span>
              </Button>
            </div>

            {/* Agent Creation Form */}
            {showAgentForm && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Create New Agent</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createAgent} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="agent-name">Name</Label>
                        <Input
                          id="agent-name"
                          value={newAgent.name}
                          onChange={(e) => setNewAgent({...newAgent, name: e.target.value})}
                          placeholder="Agent name"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="agent-email">Email</Label>
                        <Input
                          id="agent-email"
                          type="email"
                          value={newAgent.email}
                          onChange={(e) => setNewAgent({...newAgent, email: e.target.value})}
                          placeholder="agent@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="agent-role">Role</Label>
                        <Select 
                          value={newAgent.role} 
                          onValueChange={(value) => setNewAgent({...newAgent, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Agent">Agent</SelectItem>
                            <SelectItem value="Senior Agent">Senior Agent</SelectItem>
                            <SelectItem value="Team Lead">Team Lead</SelectItem>
                            <SelectItem value="Manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="agent-team">Team</Label>
                        <Select 
                          value={newAgent.team} 
                          onValueChange={(value) => setNewAgent({...newAgent, team: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select team..." />
                          </SelectTrigger>
                          <SelectContent>
                            {teams.map(team => (
                              <SelectItem key={team.id} value={team.name}>
                                <div className="flex items-center space-x-2">
                                  <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: team.color }}
                                  ></div>
                                  <span>{team.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button type="submit">Create Agent</Button>
                      <Button type="button" variant="outline" onClick={() => setShowAgentForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Agents List */}
            <div className="space-y-3">
              {agents.map((agent) => (
                <Card key={agent.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{agent.name}</h3>
                          <p className="text-sm text-slate-600">{agent.email}</p>
                          <div className="flex items-center space-x-2 text-xs text-slate-500 mt-1">
                            <span>{agent.role}</span>
                            {agent.team && (
                              <>
                                <span>•</span>
                                <Badge variant="outline" className="text-xs">
                                  {agent.team}
                                </Badge>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAgent(agent.id, agent.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">Customer Management</h2>
              <Button 
                onClick={() => setShowCustomerForm(!showCustomerForm)}
                className="flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Customer</span>
              </Button>
            </div>

            {/* Customer Creation Form */}
            {showCustomerForm && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Create New Customer</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createCustomer} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company-name">Company Name *</Label>
                        <Input
                          id="company-name"
                          value={newCustomer.company_name}
                          onChange={(e) => setNewCustomer({...newCustomer, company_name: e.target.value})}
                          placeholder="Company Name"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="po-number">PO Number *</Label>
                        <Input
                          id="po-number"
                          value={newCustomer.po_number}
                          onChange={(e) => setNewCustomer({...newCustomer, po_number: e.target.value})}
                          placeholder="PO-2024-001"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email-domains">Email Domains *</Label>
                      <Input
                        id="email-domains"
                        value={newCustomer.email_domains}
                        onChange={(e) => setNewCustomer({...newCustomer, email_domains: e.target.value})}
                        placeholder="company.com,company.net (comma separated)"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter multiple domains separated by commas
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="valid-until">Valid Until *</Label>
                        <Input
                          id="valid-until"
                          type="date"
                          value={newCustomer.valid_until}
                          onChange={(e) => setNewCustomer({...newCustomer, valid_until: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="total-manhours">Total Manhours *</Label>
                        <Input
                          id="total-manhours"
                          type="number"
                          step="0.1"
                          min="0"
                          value={newCustomer.total_manhours}
                          onChange={(e) => setNewCustomer({...newCustomer, total_manhours: e.target.value})}
                          placeholder="100.0"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="consumed-manhours">Consumed Manhours</Label>
                        <Input
                          id="consumed-manhours"
                          type="number"
                          step="0.1"
                          min="0"
                          value={newCustomer.consumed_manhours}
                          onChange={(e) => setNewCustomer({...newCustomer, consumed_manhours: e.target.value})}
                          placeholder="0.0"
                        />
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button type="submit">Create Customer</Button>
                      <Button type="button" variant="outline" onClick={() => setShowCustomerForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Customers List */}
            <div className="space-y-3">
              {customers.map((customer) => {
                const isExpired = new Date(customer.valid_until) < new Date();
                const utilizationPercent = (customer.consumed_manhours / customer.total_manhours) * 100;
                
                return (
                  <Card key={customer.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-medium text-slate-800">{customer.company_name}</h3>
                            <Badge variant="outline" className="text-xs">
                              {customer.po_number}
                            </Badge>
                            {isExpired && (
                              <Badge variant="destructive" className="text-xs">
                                Expired
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-600">
                                <strong>Domains:</strong> {customer.email_domains}
                              </p>
                              <p className="text-slate-600">
                                <strong>Valid Until:</strong> {new Date(customer.valid_until).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-slate-600">
                                <strong>Manhours:</strong> {customer.consumed_manhours}/{customer.total_manhours}
                              </p>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className={`h-2 rounded-full ${utilizationPercent > 90 ? 'bg-red-500' : utilizationPercent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                  style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                {utilizationPercent.toFixed(1)}% utilized
                              </p>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCustomer(customer.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">User Management</h2>
              <Button 
                onClick={() => setShowUserForm(!showUserForm)}
                className="flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add User</span>
              </Button>
            </div>

            {/* Add User Form */}
            {showUserForm && (
              <Card>
                <CardHeader>
                  <CardTitle>Create New User</CardTitle>
                  <CardDescription>Add a new agent or manager to the system</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="userName">Full Name</Label>
                        <Input
                          id="userName"
                          type="text"
                          value={newUser.name}
                          onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="userEmail">Email Address</Label>
                        <Input
                          id="userEmail"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="username">Username</Label>
                        <Input
                          id="username"
                          type="text"
                          value={newUser.username}
                          onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label htmlFor="userRole">Role</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {newUser.role === "agent" && (
                      <div>
                        <Label htmlFor="linkedAgent">Link to Agent Profile</Label>
                        <Select value={newUser.agent_id} onValueChange={(value) => setNewUser({...newUser, agent_id: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select agent profile (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No agent profile</SelectItem>
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.name} ({agent.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="userPassword">Password</Label>
                        <Input
                          id="userPassword"
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                          required
                          minLength={6}
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={newUser.confirm_password}
                          onChange={(e) => setNewUser({...newUser, confirm_password: e.target.value})}
                          required
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <Button type="submit">Create User</Button>
                      <Button type="button" variant="outline" onClick={() => setShowUserForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Users List */}
            <div className="grid gap-4">
              {users.map((user) => (
                <Card key={user.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-5 h-5 text-slate-600" />
                          <div>
                            <h3 className="font-semibold text-slate-800">{user.name}</h3>
                            <p className="text-sm text-slate-600">
                              {user.email} {user.username && `• @${user.username}`}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge variant={user.role === 'manager' ? 'default' : 'secondary'}>
                                {user.role}
                              </Badge>
                              {!user.is_active && (
                                <Badge variant="destructive">Inactive</Badge>
                              )}
                              {user.agent_id && (
                                <Badge variant="outline">
                                  Linked to Agent Profile
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingUser(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowPasswordReset(user.id)}
                        >
                          Reset Password
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteUser(user.id, user.name)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Edit User Modal */}
            {editingUser && (
              <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit User</DialogTitle>
                    <DialogDescription>
                      Update user information and settings
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={updateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="editUserName">Full Name</Label>
                        <Input
                          id="editUserName"
                          type="text"
                          value={editingUser.name}
                          onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="editUserEmail">Email Address</Label>
                        <Input
                          id="editUserEmail"
                          type="email"
                          value={editingUser.email}
                          onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="editUsername">Username</Label>
                        <Input
                          id="editUsername"
                          type="text"
                          value={editingUser.username || ""}
                          onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label htmlFor="editUserRole">Role</Label>
                        <Select 
                          value={editingUser.role} 
                          onValueChange={(value) => setEditingUser({...editingUser, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {editingUser.role === "agent" && (
                      <div>
                        <Label htmlFor="editLinkedAgent">Link to Agent Profile</Label>
                        <Select 
                          value={editingUser.agent_id || ""} 
                          onValueChange={(value) => setEditingUser({...editingUser, agent_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select agent profile (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No agent profile</SelectItem>
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.name} ({agent.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="userActive"
                        checked={editingUser.is_active}
                        onChange={(e) => setEditingUser({...editingUser, is_active: e.target.checked})}
                      />
                      <Label htmlFor="userActive">Active User</Label>
                    </div>

                    <div className="flex space-x-4">
                      <Button type="submit">Update User</Button>
                      <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {/* Password Reset Modal */}
            {showPasswordReset && (
              <Dialog open={!!showPasswordReset} onOpenChange={() => setShowPasswordReset(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Reset Password</DialogTitle>
                    <DialogDescription>
                      Enter a new password for this user
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={passwordResetData.new_password}
                        onChange={(e) => setPasswordResetData({...passwordResetData, new_password: e.target.value})}
                        minLength={6}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                      <Input
                        id="confirmNewPassword"
                        type="password"
                        value={passwordResetData.confirm_password}
                        onChange={(e) => setPasswordResetData({...passwordResetData, confirm_password: e.target.value})}
                        minLength={6}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="flex space-x-4">
                      <Button onClick={() => resetUserPassword(showPasswordReset)}>
                        Reset Password
                      </Button>
                      <Button variant="outline" onClick={() => setShowPasswordReset(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Email Configuration Tab */}
          <TabsContent value="email" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">Email Configuration</h2>
              <Button 
                onClick={() => setShowEmailConfigForm(!showEmailConfigForm)}
                className="flex items-center space-x-2"
              >
                <SettingsIcon className="w-4 h-4" />
                <span>Configure Email</span>
              </Button>
            </div>

            {/* Current Email Config Display */}
            {emailConfig && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Current Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600">
                        <strong>Helpdesk Email:</strong> {emailConfig.helpdesk_email}
                      </p>
                      <p className="text-slate-600">
                        <strong>SMTP Server:</strong> {emailConfig.smtp_server || 'Not configured'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-600">
                        <strong>SMTP Port:</strong> {emailConfig.smtp_port}
                      </p>
                      <p className="text-slate-600">
                        <strong>Username:</strong> {emailConfig.smtp_username || 'Not configured'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email Configuration Form */}
            {showEmailConfigForm && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Email Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={saveEmailConfig} className="space-y-4">
                    <div>
                      <Label htmlFor="helpdesk-email">Helpdesk Email Address *</Label>
                      <Input
                        id="helpdesk-email"
                        type="email"
                        value={emailConfigData.helpdesk_email}
                        onChange={(e) => setEmailConfigData({...emailConfigData, helpdesk_email: e.target.value})}
                        placeholder="helpdesk@mesprosoft.net"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This is the email address where customers will send tickets
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="smtp-server">SMTP Server</Label>
                        <Input
                          id="smtp-server"
                          value={emailConfigData.smtp_server}
                          onChange={(e) => setEmailConfigData({...emailConfigData, smtp_server: e.target.value})}
                          placeholder="smtp.gmail.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="smtp-port">SMTP Port</Label>
                        <Input
                          id="smtp-port"
                          type="number"
                          value={emailConfigData.smtp_port}
                          onChange={(e) => setEmailConfigData({...emailConfigData, smtp_port: parseInt(e.target.value)})}
                          placeholder="587"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="smtp-username">SMTP Username</Label>
                        <Input
                          id="smtp-username"
                          value={emailConfigData.smtp_username}
                          onChange={(e) => setEmailConfigData({...emailConfigData, smtp_username: e.target.value})}
                          placeholder="your-email@gmail.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="smtp-password">SMTP Password</Label>
                        <Input
                          id="smtp-password"
                          type="password"
                          value={emailConfigData.smtp_password}
                          onChange={(e) => setEmailConfigData({...emailConfigData, smtp_password: e.target.value})}
                          placeholder="••••••••"
                        />
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button type="submit">Save Configuration</Button>
                      <Button type="button" variant="outline" onClick={() => setShowEmailConfigForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Instructions */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h3 className="font-medium text-blue-800 mb-2">How Email Ticket Generation Works:</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Customers send emails to the configured helpdesk email address</li>
                  <li>• System validates sender domain against customer database</li>
                  <li>• Valid domains with active PO create tickets automatically</li>
                  <li>• Subject becomes ticket title, body becomes description</li>
                  <li>• Invalid domains receive "Unauthorized domain" auto-reply</li>
                  <li>• Expired POs receive "PO validity expired" auto-reply</li>
                </ul>
              </CardContent>
            </Card>

            {/* Email Testing Tool */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-lg text-green-800">Test Email Ticket Generation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="test-from-email">From Email</Label>
                    <Input
                      id="test-from-email"
                      type="email"
                      placeholder="user@startup-inn.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="test-subject">Subject</Label>
                    <Input
                      id="test-subject"
                      placeholder="System issue - need help"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="test-body">Email Body</Label>
                    <Textarea
                      id="test-body"
                      placeholder="Describe your issue here..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    onClick={async () => {
                      const fromEmail = document.getElementById('test-from-email').value;
                      const subject = document.getElementById('test-subject').value;
                      const body = document.getElementById('test-body').value;
                      
                      if (!fromEmail || !subject || !body) {
                        alert('Please fill all fields');
                        return;
                      }
                      
                      try {
                        const response = await axios.post(`${API}/process-email-ticket`, {
                          from_email: fromEmail,
                          subject: subject,
                          body: body
                        });
                        
                        if (response.data.ticket_created) {
                          alert(`✅ Success! Ticket created for ${response.data.customer}\nTicket ID: ${response.data.ticket_id}\nPriority: ${response.data.priority}`);
                          // Clear form
                          document.getElementById('test-from-email').value = '';
                          document.getElementById('test-subject').value = '';
                          document.getElementById('test-body').value = '';
                        } else {
                          alert(`❌ Ticket rejected: ${response.data.reason}`);
                        }
                      } catch (error) {
                        alert(`❌ Error: ${error.response?.data?.detail || error.message}`);
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    🧪 Test Email Processing
                  </Button>
                  <p className="text-xs text-green-700">
                    This simulates receiving an email and processing it into a ticket.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Management Tab */}
          <TabsContent value="email-tickets" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-800">Email-to-Ticket Management</h2>
            </div>

            <EmailToTicketManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Navigation component
const Navigation = () => {
  const location = useLocation();
  
  return (
    <nav className="bg-white border-b border-slate-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">Smart Helpdesk</h1>
        <div className="flex space-x-4">
          <Link to="/">
            <Button variant={location.pathname === "/" ? "default" : "outline"}>
              Submit Ticket
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant={location.pathname === "/dashboard" ? "default" : "outline"}>
              Ticket Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// Reports Page Component
const ReportsPage = () => {
  const { user, isManager } = useAuth();
  
  // Only managers can access this page
  if (!isManager) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only managers can access reports.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/dashboard">
              <Button className="w-full">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState("agent-timesheet");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Reports</h1>
            <p className="text-slate-600">Generate and view system reports</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="agent-timesheet">Agent Timesheet</TabsTrigger>
            <TabsTrigger value="customer-effort">Customer Effort</TabsTrigger>
          </TabsList>

          <TabsContent value="agent-timesheet" className="space-y-6">
            <AgentTimesheetReport />
          </TabsContent>

          <TabsContent value="customer-effort" className="space-y-6">
            <CustomerEffortReport />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Agent Timesheet Report Component
const AgentTimesheetReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState("all");

  useEffect(() => {
    fetchAgents();
    fetchTeams();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/reports/agents-list`);
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/reports/teams-list`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      
      if (selectedAgent && selectedAgent !== "all") params.append('agent_filter', selectedAgent);
      if (selectedTeam && selectedTeam !== "all") params.append('team_filter', selectedTeam);

      const response = await axios.get(`${API}/reports/agent-timesheet?${params}`);
      setReportData(response.data);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvData = [];
    csvData.push(['Agent Name', 'Team', 'Date', 'Ticket ID', 'Ticket Title', 'Planned Effort (hrs)', 'Actual Effort (hrs)', 'Notes']);
    
    reportData.forEach(agent => {
      agent.entries.forEach(entry => {
        csvData.push([
          entry.agent_name,
          entry.team || 'No Team',
          entry.date,
          entry.ticket_id,
          entry.ticket_title,
          entry.planned_effort.toString(),
          entry.actual_effort.toString(),
          entry.notes || ''
        ]);
      });
    });

    const csvContent = csvData.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-timesheet-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ClockIcon className="w-5 h-5" />
            <span>Agent-Wise Day-Wise Timesheet</span>
          </CardTitle>
          <CardDescription>
            View actual effort logged by agents within a specified date range
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="agent-filter">Filter by Agent</Label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger>
                  <SelectValue placeholder="All Agents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} {agent.team && `(${agent.team})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="team-filter">Filter by Team</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger>
                  <SelectValue placeholder="All Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.name} value={team.name}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex space-x-2 mb-4">
            <Button onClick={generateReport} disabled={loading}>
              {loading ? "Generating..." : "Generate Report"}
            </Button>
            {reportData.length > 0 && (
              <Button onClick={exportToCSV} variant="outline">
                Export to CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {reportData.length > 0 && (
        <div className="space-y-4">
          {reportData.map((agent) => (
            <Card key={agent.agent_id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{agent.agent_name}</span>
                  <Badge variant="outline">{agent.team || 'No Team'}</Badge>
                </CardTitle>
                <CardDescription>
                  Total Planned: {agent.total_planned_effort.toFixed(1)} hrs | 
                  Total Actual: {agent.total_actual_effort.toFixed(1)} hrs | 
                  Entries: {agent.entries.length}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Ticket ID</th>
                        <th className="text-left p-2">Ticket Title</th>
                        <th className="text-right p-2">Planned (hrs)</th>
                        <th className="text-right p-2">Actual (hrs)</th>
                        <th className="text-left p-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agent.entries.map((entry, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2">{entry.date}</td>
                          <td className="p-2 font-mono text-xs">{entry.ticket_id}</td>
                          <td className="p-2 max-w-xs truncate">{entry.ticket_title}</td>
                          <td className="p-2 text-right">{entry.planned_effort.toFixed(1)}</td>
                          <td className="p-2 text-right">{entry.actual_effort.toFixed(1)}</td>
                          <td className="p-2 max-w-xs truncate">{entry.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && reportData.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <BarChartIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No timesheet data found for the selected criteria</p>
            <p className="text-sm text-slate-500 mt-2">Try adjusting your date range or filters</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Customer Effort Report Component
const CustomerEffortReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [selectedCustomer, setSelectedCustomer] = useState("all");

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/reports/customers-list`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      
      if (selectedCustomer && selectedCustomer !== "all") params.append('customer_filter', selectedCustomer);

      const response = await axios.get(`${API}/reports/customer-effort?${params}`);
      setReportData(response.data);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvData = [];
    csvData.push([
      'Customer Name', 'Customer Email', 
      'Resolved Tickets Effort (hrs)', 'Non-Resolved Tickets Effort (hrs)',
      'Ticket ID', 'Ticket Title', 'Priority', 'Status', 'Created Date', 'Resolved Date', 'Total Effort (hrs)', 'Type'
    ]);
    
    reportData.forEach(customer => {
      // Add resolved tickets
      customer.resolved_tickets.forEach(ticket => {
        csvData.push([
          customer.customer_name,
          customer.customer_email,
          customer.resolved_tickets_effort.toString(),
          customer.non_resolved_tickets_effort.toString(),
          ticket.ticket_id,
          ticket.ticket_title,
          ticket.priority,
          ticket.status,
          new Date(ticket.created_at).toLocaleDateString(),
          ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleDateString() : '',
          ticket.total_actual_effort.toString(),
          'Resolved'
        ]);
      });
      
      // Add non-resolved tickets
      customer.non_resolved_tickets.forEach(ticket => {
        csvData.push([
          customer.customer_name,
          customer.customer_email,
          customer.resolved_tickets_effort.toString(),
          customer.non_resolved_tickets_effort.toString(),
          ticket.ticket_id,
          ticket.ticket_title,
          ticket.priority,
          ticket.status,
          new Date(ticket.created_at).toLocaleDateString(),
          ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleDateString() : '',
          ticket.total_actual_effort.toString(),
          'Non-Resolved'
        ]);
      });
    });

    const csvContent = csvData.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-effort-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <UserIcon className="w-5 h-5" />
            <span>Customer-Wise Effort Report</span>
          </CardTitle>
          <CardDescription>
            View actual efforts for resolved and non-resolved tickets by customer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Label htmlFor="start-date-customer">Start Date</Label>
              <Input
                id="start-date-customer"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end-date-customer">End Date</Label>
              <Input
                id="end-date-customer"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="customer-filter">Filter by Customer</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger>
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.email} value={customer.email}>
                      {customer.name} ({customer.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex space-x-2 mb-4">
            <Button onClick={generateReport} disabled={loading}>
              {loading ? "Generating..." : "Generate Report"}
            </Button>
            {reportData.length > 0 && (
              <Button onClick={exportToCSV} variant="outline">
                Export to CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {reportData.length > 0 && (
        <div className="space-y-4">
          {reportData.map((customer, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle>{customer.customer_name}</CardTitle>
                <CardDescription>{customer.customer_email}</CardDescription>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-600">Resolved Tickets Effort</p>
                    <p className="text-lg font-bold text-green-700">
                      {customer.resolved_tickets_effort.toFixed(1)} hrs
                    </p>
                    <p className="text-xs text-green-600">
                      ({customer.resolved_tickets.length} tickets)
                    </p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-sm text-orange-600">Non-Resolved Tickets Effort</p>
                    <p className="text-lg font-bold text-orange-700">
                      {customer.non_resolved_tickets_effort.toFixed(1)} hrs
                    </p>
                    <p className="text-xs text-orange-600">
                      ({customer.non_resolved_tickets.length} tickets)
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="resolved" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="resolved">
                      Resolved Tickets ({customer.resolved_tickets.length})
                    </TabsTrigger>
                    <TabsTrigger value="non-resolved">
                      Non-Resolved Tickets ({customer.non_resolved_tickets.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="resolved" className="mt-4">
                    {customer.resolved_tickets.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Ticket ID</th>
                              <th className="text-left p-2">Title</th>
                              <th className="text-center p-2">Priority</th>
                              <th className="text-center p-2">Status</th>
                              <th className="text-center p-2">Created</th>
                              <th className="text-center p-2">Resolved</th>
                              <th className="text-right p-2">Effort (hrs)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customer.resolved_tickets.map((ticket) => (
                              <tr key={ticket.ticket_id} className="border-b">
                                <td className="p-2 font-mono text-xs">{ticket.ticket_id}</td>
                                <td className="p-2 max-w-xs truncate">{ticket.ticket_title}</td>
                                <td className="p-2 text-center">
                                  <Badge className={getPriorityBadge(ticket.priority)}>
                                    {ticket.priority}
                                  </Badge>
                                </td>
                                <td className="p-2 text-center">
                                  <Badge variant="outline">{ticket.status}</Badge>
                                </td>
                                <td className="p-2 text-center text-xs">{formatDate(ticket.created_at)}</td>
                                <td className="p-2 text-center text-xs">
                                  {ticket.resolved_at ? formatDate(ticket.resolved_at) : '-'}
                                </td>
                                <td className="p-2 text-right font-medium">
                                  {ticket.total_actual_effort.toFixed(1)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center py-4 text-slate-500">No resolved tickets</p>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="non-resolved" className="mt-4">
                    {customer.non_resolved_tickets.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Ticket ID</th>
                              <th className="text-left p-2">Title</th>
                              <th className="text-center p-2">Priority</th>
                              <th className="text-center p-2">Status</th>
                              <th className="text-center p-2">Created</th>
                              <th className="text-right p-2">Effort (hrs)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customer.non_resolved_tickets.map((ticket) => (
                              <tr key={ticket.ticket_id} className="border-b">
                                <td className="p-2 font-mono text-xs">{ticket.ticket_id}</td>
                                <td className="p-2 max-w-xs truncate">{ticket.ticket_title}</td>
                                <td className="p-2 text-center">
                                  <Badge className={getPriorityBadge(ticket.priority)}>
                                    {ticket.priority}
                                  </Badge>
                                </td>
                                <td className="p-2 text-center">
                                  <Badge variant="outline">{ticket.status}</Badge>
                                </td>
                                <td className="p-2 text-center text-xs">{formatDate(ticket.created_at)}</td>
                                <td className="p-2 text-right font-medium">
                                  {ticket.total_actual_effort.toFixed(1)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center py-4 text-slate-500">No non-resolved tickets</p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && reportData.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <UserIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No customer effort data found for the selected criteria</p>
            <p className="text-sm text-slate-500 mt-2">Try adjusting your date range or filters</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<CustomerForm />} />
            <Route path="/login" element={<Login />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <TicketDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customization" 
              element={
                <ProtectedRoute>
                  <CustomizationPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
};

// Canned Response Modal Component
const CannedResponseModal = ({ isOpen, onClose, onSelectResponse }) => {
  const [cannedResponses, setCannedResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newResponse, setNewResponse] = useState({
    title: "",
    content: "",
    category: "General",
    is_public: true
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCannedResponses();
    }
  }, [isOpen]);

  const fetchCannedResponses = async () => {
    try {
      const response = await axios.get(`${API}/canned-responses`);
      setCannedResponses(response.data);
    } catch (error) {
      console.error("Error fetching canned responses:", error);
    } finally {
      setLoading(false);
    }
  };

  const createCannedResponse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/canned-responses`, newResponse);
      setNewResponse({ title: "", content: "", category: "General", is_public: true });
      setShowAddForm(false);
      fetchCannedResponses();
    } catch (error) {
      console.error("Error creating canned response:", error);
    }
  };

  const deleteCannedResponse = async (responseId) => {
    if (window.confirm("Are you sure you want to delete this canned response?")) {
      try {
        await axios.delete(`${API}/canned-responses/${responseId}`);
        fetchCannedResponses();
      } catch (error) {
        console.error("Error deleting canned response:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <MessageSquareIcon className="w-5 h-5" />
            <span>Canned Responses</span>
          </DialogTitle>
          <DialogDescription>
            Select a canned response to use or create a new one
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex space-x-2 mb-4">
          <Button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-2"
            variant="outline"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add New Response</span>
          </Button>
        </div>

        {showAddForm && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Create New Canned Response</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createCannedResponse} className="space-y-4">
                <div>
                  <Label htmlFor="response-title">Title</Label>
                  <Input
                    id="response-title"
                    value={newResponse.title}
                    onChange={(e) => setNewResponse({...newResponse, title: e.target.value})}
                    placeholder="Response title"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="response-content">Content</Label>
                  <Textarea
                    id="response-content"
                    value={newResponse.content}
                    onChange={(e) => setNewResponse({...newResponse, content: e.target.value})}
                    placeholder="Response content"
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="response-category">Category</Label>
                  <Select 
                    value={newResponse.category} 
                    onValueChange={(value) => setNewResponse({...newResponse, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Technical">Technical</SelectItem>
                      <SelectItem value="Support">Support</SelectItem>
                      <SelectItem value="Finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex space-x-2">
                  <Button type="submit">Create Response</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="text-center py-8">Loading canned responses...</div>
          ) : (
            <div className="space-y-3">
              {cannedResponses.map((response) => (
                <Card key={response.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium text-slate-800">{response.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {response.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {response.content}
                        </p>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            onClick={() => {
                              onSelectResponse(response.content);
                              onClose();
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Use Response
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteCannedResponse(response.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Ticket Edit Modal Component
const TicketEditModal = ({ isOpen, onClose, ticket, agents, teams, onUpdate }) => {
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    priority: "Medium",
    status: "Open",
    assigned_to: "",
    assigned_team: "",
    customer_name: "",
    customer_email: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);

  useEffect(() => {
    if (ticket && isOpen) {
      setEditForm({
        title: ticket.title || "",
        description: ticket.description || "",
        priority: ticket.priority || "Medium",
        status: ticket.status || "Open",
        assigned_to: ticket.assigned_to || "",
        assigned_team: ticket.assigned_team || "",
        customer_name: ticket.customer_name || "",
        customer_email: ticket.customer_email || ""
      });
      setUpdateStatus(null);
    }
  }, [ticket, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ticket) return;

    setIsSubmitting(true);
    setUpdateStatus(null);

    try {
      // Update ticket via API
      await axios.patch(`${API}/tickets/${ticket.id}`, editForm);
      
      setUpdateStatus({
        type: 'success',
        message: '✅ Ticket updated successfully!'
      });

      // Call the onUpdate callback to refresh the ticket list
      if (onUpdate) {
        onUpdate();
      }

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error("Error updating ticket:", error);
      setUpdateStatus({
        type: 'error',
        message: `❌ Failed to update ticket: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // Auto-assign team when agent is selected
      if (field === 'assigned_to' && value && value !== 'unassigned') {
        const selectedAgent = agents.find(agent => agent.id === value);
        if (selectedAgent && selectedAgent.team) {
          newForm.assigned_team = selectedAgent.team;
        }
      }
      
      // Clear team when agent is unassigned
      if (field === 'assigned_to' && (!value || value === 'unassigned')) {
        newForm.assigned_team = '';
      }
      
      return newForm;
    });
  };

  if (!isOpen || !ticket) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <SettingsIcon className="w-5 h-5" />
            <span>Edit Ticket #{ticket.id?.substring(0, 8)}</span>
          </DialogTitle>
          <DialogDescription>
            Update ticket information and assignment details
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto max-h-[70vh] px-1">
          {/* Update Status Display */}
          {updateStatus && (
            <Alert className={updateStatus.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <AlertDescription className={updateStatus.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {updateStatus.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Auto-assignment notification */}
          {editForm.assigned_to && editForm.assigned_to !== 'unassigned' && editForm.assigned_team && (
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800 text-sm">
                ℹ️ Team "{editForm.assigned_team}" was automatically assigned based on the selected agent's team.
              </AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-6">
            <div>
              <Label htmlFor="edit-title" className="text-sm font-medium">
                Ticket Title *
              </Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter ticket title"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-description" className="text-sm font-medium">
                Description *
              </Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the issue or request"
                rows={4}
                required
                className="mt-1 resize-none"
              />
            </div>
          </div>

          {/* Customer Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-customer-name" className="text-sm font-medium">
                Customer Name *
              </Label>
              <Input
                id="edit-customer-name"
                value={editForm.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                placeholder="Customer name"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-customer-email" className="text-sm font-medium">
                Customer Email *
              </Label>
              <Input
                id="edit-customer-email"
                type="email"
                value={editForm.customer_email}
                onChange={(e) => handleInputChange('customer_email', e.target.value)}
                placeholder="customer@example.com"
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* Status and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-priority" className="text-sm font-medium">
                Priority
              </Label>
              <Select 
                value={editForm.priority} 
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span>Low Priority</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Medium">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Medium Priority</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="High">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>High Priority</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Critical">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Critical Priority</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-status" className="text-sm font-medium">
                Status
              </Label>
              <Select 
                value={editForm.status} 
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">
                    <div className="flex items-center space-x-2">
                      <AlertTriangleIcon className="w-4 h-4 text-red-500" />
                      <span>Open</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="In Progress">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-4 h-4 text-yellow-500" />
                      <span>In Progress</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Resolved">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span>Resolved</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Closed">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="w-4 h-4 text-gray-500" />
                      <span>Closed</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-assigned-agent" className="text-sm font-medium">
                Assigned Agent
              </Label>
              <Select 
                value={editForm.assigned_to || "unassigned"} 
                onValueChange={(value) => handleInputChange('assigned_to', value === "unassigned" ? "" : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select agent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <span className="text-gray-500">Unassigned</span>
                  </SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{agent.name}</span>
                        <span className="text-xs text-gray-500">({agent.team || 'No team'})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-assigned-team" className="text-sm font-medium">
                Assigned Team
                {editForm.assigned_to && editForm.assigned_to !== 'unassigned' && (
                  <span className="text-xs text-blue-600 ml-2">(Auto-assigned from agent)</span>
                )}
              </Label>
              <Select 
                value={editForm.assigned_team || "unassigned"} 
                onValueChange={(value) => handleInputChange('assigned_team', value === "unassigned" ? "" : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select team..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <span className="text-gray-500">No team</span>
                  </SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.name}>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: team.color }}
                        ></div>
                        <span className="font-medium">{team.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ticket Metadata */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Ticket Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Ticket ID:</span>
                <span className="ml-2 font-mono">{ticket.id}</span>
              </div>
              <div>
                <span className="text-gray-600">Created:</span>
                <span className="ml-2">{new Date(ticket.created_at).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Last Updated:</span>
                <span className="ml-2">{new Date(ticket.updated_at).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Current Agent:</span>
                <span className="ml-2">{ticket.assigned_agent_name || 'Unassigned'}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Update Ticket
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Ticket Response Section Component
const TicketResponseSection = ({ ticketId }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newResponse, setNewResponse] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    if (isExpanded) {
      fetchResponses();
    }
  }, [isExpanded, ticketId]);

  const fetchResponses = async () => {
    try {
      const response = await axios.get(`${API}/ticket-responses/ticket/${ticketId}`);
      setResponses(response.data);
    } catch (error) {
      console.error("Error fetching responses:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async (e) => {
    e.preventDefault();
    if (!newResponse.trim()) return;

    setIsSubmitting(true);
    setEmailStatus(null);
    
    try {
      const responseData = {
        ticket_id: ticketId,
        response_text: newResponse,
        is_internal: false
      };
      
      await axios.post(`${API}/ticket-responses`, responseData);
      setNewResponse("");
      fetchResponses(); // Refresh the responses
    } catch (error) {
      console.error("Error submitting response:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendEmailResponse = async (e) => {
    e.preventDefault();
    if (!newResponse.trim()) return;

    setIsSendingEmail(true);
    setEmailStatus(null);
    
    try {
      const emailData = {
        ticket_id: ticketId,
        response_text: newResponse,
        send_email: true
      };
      
      const response = await axios.post(`${API}/ticket-responses/email`, emailData);
      
      setEmailStatus({
        type: 'success',
        message: `✅ ${response.data.message}`
      });
      
      setNewResponse("");
      fetchResponses(); // Refresh the responses
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus({
        type: 'error',
        message: `❌ Failed to send email: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const deleteResponse = async (responseId) => {
    if (window.confirm("Are you sure you want to delete this response?")) {
      try {
        await axios.delete(`${API}/ticket-responses/${responseId}`);
        fetchResponses(); // Refresh the responses
      } catch (error) {
        console.error("Error deleting response:", error);
      }
    }
  };

  return (
    <div className="mt-4 border-t pt-4">
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 mb-4 text-sm"
      >
        <MessageSquareIcon className="w-4 h-4" />
        <span>{isExpanded ? 'Hide' : 'Show'} Responses ({responses.length})</span>
      </Button>

      {isExpanded && (
        <div className="space-y-4">
          {/* Response Form */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <form className="space-y-3">
                <Label htmlFor={`response-${ticketId}`} className="text-sm font-medium">
                  Add Response to Customer
                </Label>
                <Textarea
                  id={`response-${ticketId}`}
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  placeholder="Type your response to the customer here..."
                  rows={3}
                  className="resize-none"
                />
                
                {/* Email Status Display */}
                {emailStatus && (
                  <Alert className={emailStatus.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                    <AlertDescription className={emailStatus.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                      {emailStatus.message}
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Choose to save internally or send email to customer
                  </span>
                  <div className="flex space-x-2">
                    <Button 
                      type="button"
                      onClick={submitResponse}
                      size="sm" 
                      variant="outline"
                      disabled={isSubmitting || isSendingEmail || !newResponse.trim()}
                      className="border-gray-300"
                    >
                      {isSubmitting ? "Saving..." : "Save Response"}
                    </Button>
                    <Button 
                      type="button"
                      onClick={sendEmailResponse}
                      size="sm" 
                      disabled={isSubmitting || isSendingEmail || !newResponse.trim()}
                      className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-1"
                    >
                      {isSendingEmail ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>📧</span>
                          <span>Send Email</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Responses History */}
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-4 text-sm text-gray-500">
                Loading responses...
              </div>
            ) : responses.length === 0 ? (
              <div className="text-center py-4 text-sm text-gray-500">
                No responses yet. Be the first to respond!
              </div>
            ) : (
              responses.map((response) => (
                <Card key={response.id} className="hover:shadow-sm">
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            {response.agent_name || 'System'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(response.created_at).toLocaleString()}
                          </span>
                          {response.is_internal && (
                            <Badge variant="secondary" className="text-xs">
                              Internal
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            📧 Emailed
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {response.response_text}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteResponse(response.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const EffortTrackingModal = ({ isOpen, onClose, ticketId }) => {
  const [effortLogs, setEffortLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEffort, setNewEffort] = useState({
    planned_effort: "",
    actual_effort: "",
    notes: ""
  });

  useEffect(() => {
    if (isOpen && ticketId) {
      fetchEffortLogs();
    }
  }, [isOpen, ticketId]);

  const fetchEffortLogs = async () => {
    try {
      const response = await axios.get(`${API}/effort-logs/ticket/${ticketId}`);
      setEffortLogs(response.data);
    } catch (error) {
      console.error("Error fetching effort logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const createEffortLog = async (e) => {
    e.preventDefault();
    try {
      const effortData = {
        ticket_id: ticketId,
        planned_effort: parseFloat(newEffort.planned_effort),
        actual_effort: parseFloat(newEffort.actual_effort),
        notes: newEffort.notes
      };
      
      await axios.post(`${API}/effort-logs`, effortData);
      setNewEffort({ planned_effort: "", actual_effort: "", notes: "" });
      fetchEffortLogs();
    } catch (error) {
      console.error("Error creating effort log:", error);
    }
  };

  const getTotalEffort = () => {
    return effortLogs.reduce((total, log) => ({
      planned: total.planned + log.planned_effort,
      actual: total.actual + log.actual_effort
    }), { planned: 0, actual: 0 });
  };

  if (!isOpen) return null;

  const totals = getTotalEffort();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <TimerIcon className="w-5 h-5" />
            <span>Effort Tracking</span>
          </DialogTitle>
          <DialogDescription>
            Log and track planned vs actual effort for this ticket
          </DialogDescription>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{totals.planned.toFixed(1)}h</p>
                <p className="text-sm text-gray-600">Total Planned</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{totals.actual.toFixed(1)}h</p>
                <p className="text-sm text-gray-600">Total Actual</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add New Effort Log */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg">Log New Effort</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={createEffortLog} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="planned-effort">Planned Effort (hours)</Label>
                  <Input
                    id="planned-effort"
                    type="number"
                    step="0.1"
                    min="0"
                    value={newEffort.planned_effort}
                    onChange={(e) => setNewEffort({...newEffort, planned_effort: e.target.value})}
                    placeholder="2.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="actual-effort">Actual Effort (hours)</Label>
                  <Input
                    id="actual-effort"
                    type="number"
                    step="0.1"
                    min="0"
                    value={newEffort.actual_effort}
                    onChange={(e) => setNewEffort({...newEffort, actual_effort: e.target.value})}
                    placeholder="3.0"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="effort-notes">Notes (optional)</Label>
                <Textarea
                  id="effort-notes"
                  value={newEffort.notes}
                  onChange={(e) => setNewEffort({...newEffort, notes: e.target.value})}
                  placeholder="Additional notes about the effort..."
                  rows={2}
                />
              </div>
              <Button type="submit" className="w-full">Log Effort</Button>
            </form>
          </CardContent>
        </Card>

        {/* Effort Logs History */}
        <div className="overflow-y-auto max-h-60">
          <h4 className="font-medium mb-3">Effort History</h4>
          {loading ? (
            <div className="text-center py-4">Loading effort logs...</div>
          ) : effortLogs.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No effort logs yet</div>
          ) : (
            <div className="space-y-2">
              {effortLogs.map((log) => (
                <Card key={log.id} className="hover:shadow-sm">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm">
                          <span className="font-medium text-blue-600">{log.planned_effort}h planned</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium text-green-600">{log.actual_effort}h actual</span>
                        </div>
                        {log.notes && (
                          <div className="text-xs text-gray-600 italic">
                            "{log.notes}"
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default App;